"use client"

import type { ElementType } from "react"
import { NavLink, Stack, ScrollArea, Group, Badge } from "@forestsync/ui"
import { useTranslations } from "next-intl"
import { usePathname } from "next/navigation"
import Link from "next/link"
import {
  TbWallet,
  TbShoppingCart,
  TbActivity,
  TbSettings,
  TbTrees,
  TbCoin,
  TbCreditCard,
  TbWorldCheck,
  TbShieldCheck,
  TbAlertCircle,
  TbArrowsExchange,
  TbBriefcase,
} from "react-icons/tb"
import { USER_ROLES } from "@forestsync/db"

type NavTab = { key: string; href: string; icon: ElementType }

interface DashboardNavProps {
  onNavigate?: () => void
  orgRole?: number | null
  stripeOnboardingComplete?: boolean | null
  stripeRestricted?: boolean
  tradingEnabled?: boolean
  viewOnly?: boolean
  defaultMarketHref?: string
}

export function DashboardNav({ onNavigate, orgRole, stripeOnboardingComplete, stripeRestricted, tradingEnabled, viewOnly, defaultMarketHref = "/market" }: DashboardNavProps) {
  const t = useTranslations("dashboard.nav")
  const pathname = usePathname()

  const isPD = orgRole === USER_ROLES.PROJECT_DEVELOPER || orgRole === USER_ROLES.BOTH
  const isBroker = orgRole === USER_ROLES.BROKER
  // One verification item for PDs and traders alike — hidden once the account is
  // verified and healthy, back with "Action needed" when Stripe pauses the account
  const showVerification = (isPD || tradingEnabled) && (!stripeOnboardingComplete || stripeRestricted)

  // Brokers don't own credits, sinks, or place CLOB orders — their whole surface is the
  // option board (public /market/options) plus their own contracts/settings.
  //
  // viewOnly accounts (MarketWatch observers and merchant dashboard viewers alike —
  // registrationData.viewOnly, provisioned via createObserverAccount/
  // provisionMerchantDashboardAccount) never place orders, have no trading activity, and own
  // no credits in their own name — Orders/Activity/My Credits are all buyer-only concepts that
  // don't apply. Portfolio stays: for a merchant it's their own real position
  // (MerchantDashboardView); for a generic observer it currently falls through to the ordinary
  // (empty) BuyerDashboard — a pre-existing rough edge, not something this trims further.
  const tabs: NavTab[] = isBroker
    ? [
        { key: "optionBoard", href: "/market/options", icon: TbBriefcase },
        { key: "myContracts", href: "/dashboard/options", icon: TbArrowsExchange },
        { key: "settings", href: "/dashboard/settings", icon: TbSettings },
      ]
    : [
        { key: "portfolio", href: "/dashboard", icon: TbWallet },
        ...(isPD ? [{ key: "sinks", href: "/dashboard/sinks", icon: TbTrees }] : []),
        ...(isPD ? [{ key: "registryProfile", href: "/dashboard/registry-profile", icon: TbWorldCheck }] : []),
        ...(viewOnly ? [] : [{ key: "orders", href: "/dashboard/orders", icon: TbShoppingCart }]),
        ...(viewOnly ? [] : [{ key: "activity", href: "/dashboard/activity", icon: TbActivity }]),
        ...(viewOnly ? [] : [{ key: "credits", href: "/dashboard/credits", icon: TbCoin }]),
        ...(isPD ? [{ key: "earnings", href: "/dashboard/payouts", icon: TbCreditCard }] : []),
        ...(isPD ? [{ key: "brokerOptions", href: "/dashboard/options", icon: TbBriefcase }] : []),
        { key: "settings", href: "/dashboard/settings", icon: TbSettings },
      ]

  function isActive(href: string) {
    if (href === "/dashboard") return pathname === "/dashboard"
    return pathname.startsWith(href)
  }

  function renderTab(tab: NavTab) {
    return (
      <NavLink
        key={tab.key}
        label={t(tab.key as Parameters<typeof t>[0])}
        leftSection={<tab.icon size={18} />}
        active={isActive(tab.href)}
        component={Link}
        href={tab.href}
        onClick={onNavigate}
        variant="light"
      />
    )
  }

  return (
    <ScrollArea h="100%">
      <Stack gap={4} p="sm">
        {/* viewOnly accounts (merchant dashboards, generic observers) never trade, and the top
            bar (MarketplaceShell) already has an unconditional "Market" link to the same
            defaultMarketHref — this sidebar shortcut is pure duplication for them specifically. */}
        {(tradingEnabled || !!stripeOnboardingComplete) && !viewOnly && (
          <NavLink
            label={t("terminal")}
            leftSection={<TbArrowsExchange size={18} />}
            component={Link}
            href={defaultMarketHref}
            onClick={onNavigate}
            c="teal"
            variant="light"
          />
        )}
        {tabs.map(renderTab)}
        {showVerification && (
          <NavLink
            label={
              <Group gap={4} wrap="nowrap">
                <span>{t("accountVerification")}</span>
                {stripeOnboardingComplete && stripeRestricted
                  ? <Badge size="xs" color="red" variant="light">{t("actionNeeded")}</Badge>
                  : <TbAlertCircle size={14} color="var(--mantine-color-yellow-6)" />
                }
              </Group>
            }
            leftSection={<TbShieldCheck size={18} />}
            active={isActive("/onboarding/stripe")}
            component={Link}
            href="/onboarding/stripe"
            onClick={onNavigate}
            variant="light"
          />
        )}
      </Stack>
    </ScrollArea>
  )
}
